import {
  Badge,
  Button,
  Card,
  FlexContent,
  FormField,
  MessageBox,
  ModalDialog,
  Select,
  Switch,
  TableCell,
  TableHeadCell,
  TableRow,
  Tabs,
  useDebounce,
} from "@rothoblaas/design-system";
import { FC, useContext, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "../../components/ui/Header";
import Table from "../../components/ui/Table";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { ACCESS_SCOPE_TITLES, ACCESS_SCOPES, AccessRule, AccessTarget } from "../../http/types/accessControl";
import { SearchModule } from "../../http/types/modules";
import { getAccessTargetKey, useAccessControlApi } from "../../http/useAccessControl";
import { useModulesApi } from "../../http/useModules";
import { PaginationQuery } from "../../types";
import { getDeleteAction, getEditAction } from "../../utils/rowActions";
import AccessRuleModal from "./AccessRuleModal";
import styles from "./AdminAccessControl.module.css";
import ImportAccessRulesModal from "./ImportAccessRulesModal";

const AdminAccessControl: FC = () => {
  const i18n = useI18n();

  return (
    <>
      <Header title={i18n.translation.accessControl.plural} path={{ display: i18n.translation.accessControl.plural }} />

      <FlexContent padding noPaddingTop direction="column">
        <MessageBox
          type="informative"
          content={i18n.translation.accessControl.disclaimer}
          className={styles.disclaimer}
        />
        {/* One tab per app, then the modules' one. */}
        <Tabs
          renderMode="lazy"
          urlParameter="scope"
          hideBorderBottom
          tabs={[
            ...ACCESS_SCOPES.map((scope) => ({ title: ACCESS_SCOPE_TITLES[scope] })),
            { title: i18n.translation.accessControl.modules },
          ]}
          contents={[
            ...ACCESS_SCOPES.map((scope) => <AccessControlPanel key={scope} target={{ scope }} filterPrefix={scope} />),
            <AccessControlModulesPanel key="modules" />,
          ]}
        />
      </FlexContent>
    </>
  );
};

// The URL parameter of the module picked in the modules' tab, and the prefix of its rules' filters.
const MODULE_PARAMETER = "module";
const MODULE_FILTERS_PREFIX = "module_rules";

// The modules' tab: the access control of the module picked on top, the same as an app's.
const AccessControlModulesPanel: FC = () => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const [searchParams, setSearchParams] = useSearchParams();
  const moduleId = searchParams.get(MODULE_PARAMETER) ?? undefined;

  // every module (no pagination parameters), by name
  const { data: modules } = useModulesApi().useAdminModules({}, aborter.current.signal);
  const moduleOptions = useMemo(
    () => [...(modules?.items ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [modules],
  );

  const selectModule = (id: string | undefined) =>
    setSearchParams((params) => {
      const next = new URLSearchParams(params);
      // the previous module's search and page don't apply to the next one
      [...next.keys()].filter((key) => key.startsWith(`${MODULE_FILTERS_PREFIX}_`)).forEach((key) => next.delete(key));
      if (id) {
        next.set(MODULE_PARAMETER, id);
      } else {
        next.delete(MODULE_PARAMETER);
      }
      return next;
    });

  return (
    <FlexContent direction="column">
      <MessageBox
        type="informative"
        content={i18n.translation.accessControl.modulesDisclaimer}
        className={styles.disclaimer}
      />
      <div className={styles.moduleSelect}>
        <FormField label={i18n.translation.modules.singular}>
          {({ labelId }) => (
            <Select<SearchModule, false>
              inputId={labelId}
              options={moduleOptions}
              isLoading={!modules}
              placeholder={`${i18n.translation.common.select}...`}
              getOptionLabel={(x) => x.name}
              getOptionValue={(x) => x.id}
              value={moduleOptions.find((x) => x.id === moduleId) ?? null}
              onChange={(option: SearchModule | null) => selectModule(option?.id)}
            />
          )}
        </FormField>
      </div>

      {!!moduleId && (
        <AccessControlPanel key={moduleId} target={{ moduleId }} filterPrefix={MODULE_FILTERS_PREFIX} />
      )}
    </FlexContent>
  );
};

// The lock switch and the rules of an app or of a module.
const AccessControlPanel: FC<{ target: AccessTarget; filterPrefix: string }> = ({ target, filterPrefix }) => {
  const i18n = useI18n();
  const canManageUsers = useAuthContext().permissions.admin.canManageUsers;
  const dispatch = useContext(NotificationDispatch);
  const accessControlApi = useAccessControlApi();
  const aborter = useRef(new AbortController());
  const filters = useListFilters({ isSearchable: true, prefix: filterPrefix });
  const debouncedValue = useDebounce(filters.state.q);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const [isLockSubmitting, setIsLockSubmitting] = useState(false);
  const [pendingLockValue, setPendingLockValue] = useState<boolean>();
  const [ruleToEdit, setRuleToEdit] = useState<AccessRule>();
  const [ruleToDelete, setRuleToDelete] = useState<AccessRule>();
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [isImportingRules, setIsImportingRules] = useState(false);
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  const { data: settings, error: settingsError } = accessControlApi.getSettings(target, aborter.current.signal);
  const { data: rules, error: rulesError } = accessControlApi.getRules(target, query, aborter.current.signal);
  const lockTexts =
    "moduleId" in target ? i18n.translation.accessControl.moduleSettings : i18n.translation.accessControl.settings;

  const isInitialized = useInitializer((settings && rules) || settingsError || rulesError);

  if (!isInitialized) {
    return null;
  }

  const confirmLockChange = (locked: boolean) => {
    setIsLockSubmitting(true);
    accessControlApi
      .updateSettings(target, { locked })
      .then(() => {
        dispatch(showSuccessNotification(i18n.translation));
        setPendingLockValue(undefined);
      })
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsLockSubmitting(false));
  };

  return (
    <FlexContent direction="column">
      <Card shadow style={{ marginBottom: "1em", padding: "0.75em 1em" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5em", width: "100%" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1em" }}>
            <div>{lockTexts.lockSwitchLabel}</div>
            <Switch
              id={`lock-switch-${getAccessTargetKey(target)}`}
              checked={!!settings?.locked}
              disabled={!settings || isLockSubmitting}
              onChange={(checked) => setPendingLockValue(checked)}
              // red only while locked: the color applies to the switch's "on" state
              color="var(--red)"
            />
          </div>
          <MessageBox type="warning" content={lockTexts.lockSwitchDescription} />
        </div>
      </Card>

      <Table<AccessRule>
        filters={filters}
        search={{
          placeholder: i18n.translation.accessControl.rules.search,
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.email}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.user}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.status}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.reason}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.createdAt}</TableHeadCell>
            <TableHeadCell></TableHeadCell>
          </TableRow>
        }
        headingActions={
          <>
            <Button variant="secondary" onClick={() => setIsImportingRules(true)}>
              {i18n.translation.accessControl.rules.import}
            </Button>
            <Button onClick={() => setIsAddingRule(true)}>{i18n.translation.accessControl.rules.createNew}</Button>
          </>
        }
        items={rules?.items}
        total={rules?.total}
        error={!!rulesError}
        renderRow={(rule) => (
          <>
            <TableCell>{rule.email}</TableCell>
            <TableCell>
              {rule.user &&
                (canManageUsers ? <Link to={`/admin/users/${rule.user.id}`}>{rule.user.email}</Link> : rule.user.email)}
            </TableCell>
            <TableCell>
              <Badge color={rule.status === "allowed" ? "success" : "error"}>
                {rule.status === "allowed"
                  ? i18n.translation.accessControl.rules.statusAllowed
                  : i18n.translation.accessControl.rules.statusBlocked}
              </Badge>
            </TableCell>
            <TableCell>{rule.reason}</TableCell>
            <TableCell>{new Date(rule.createdAt).toLocaleDateString()}</TableCell>
          </>
        )}
        rowActions={(rule) => [
          getEditAction(i18n, () => setRuleToEdit(rule)),
          getDeleteAction(i18n, () => setRuleToDelete(rule), true),
        ]}
      />

      {pendingLockValue !== undefined && (
        <ModalDialog
          onClose={() => setPendingLockValue(undefined)}
          title={lockTexts.lockSwitchLabel}
          isSubmitting={isLockSubmitting}
          buttons={[
            <Button key={0} onClick={() => setPendingLockValue(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button key={1} onClick={() => confirmLockChange(pendingLockValue)}>
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {pendingLockValue ? lockTexts.confirmEnable : lockTexts.confirmDisable}
        </ModalDialog>
      )}

      {(isAddingRule || !!ruleToEdit) && (
        <AccessRuleModal
          target={target}
          rule={ruleToEdit}
          onClose={() => {
            setIsAddingRule(false);
            setRuleToEdit(undefined);
          }}
        />
      )}

      {isImportingRules && <ImportAccessRulesModal target={target} onClose={() => setIsImportingRules(false)} />}

      {!!ruleToDelete && (
        <ModalDialog
          onClose={() => setRuleToDelete(undefined)}
          title={i18n.translation.accessControl.rules.delete}
          isSubmitting={isDeleteSubmitting}
          buttons={[
            <Button key={0} onClick={() => setRuleToDelete(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key={1}
              onClick={() => {
                setIsDeleteSubmitting(true);
                accessControlApi
                  .removeRule(target, ruleToDelete.id)
                  .then(() => setRuleToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsDeleteSubmitting(false));
              }}
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {i18n.translation.accessControl.rules.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default AdminAccessControl;
